import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { Phone } from 'components/svg';
import { Button } from 'components/core/Button';
import { PhoneNumber, PhoneNumberButton } from 'components/contact/PhoneNumber';

import './ClickToShowNumberButton.scss';


export function ClickToShowNumberButton({
	className,
	callToActionText,
	showAsLink,
	hideIcon,
	outline,
	size,
	color,
	dark,
	OMIAppRentalCompleted,
	facilityPhone,
	unformattedFacilityPhone,
	fetchPhoneNumber,
	// sf_12700_sfdc_paid_landing_refresh
	label,
	mobileLabel,
	parentPhoneNumber,
	parentPhoneLoaded,
	setParentPhoneNumber,
	setParentPhoneLoaded
}) {
	const [fetchingPhoneNumber, setFetchingPhoneNumber] = useState(false);
	const [phoneNumberLoaded, setPhoneNumberLoaded] = useState(false);
	const [outlineLoadingBtn, setOutlineLoadingBtn] = useState(outline);
	const [formattedPhoneNumber, setFormattedPhoneNumber] = useState(null);
	const [unformattedPhoneNumber, setUnformattedPhoneNumber] = useState(null);

	// sf_12700_sfdc_paid_landing_refresh
	useEffect(() => {
		if (parentPhoneLoaded) {
			setPhoneNumberLoaded(true);
		}
	}, [parentPhoneLoaded]);

	const handleFetchPhoneNumber = () => {
		setOutlineLoadingBtn(false);
		setFetchingPhoneNumber(true);
		return fetchPhoneNumber()
			.then(({ payload }) => {
				setOutlineLoadingBtn(outline);
				setFetchingPhoneNumber(false);

				const { number: newFormattedPhone, unformattedNumber: newUnformattedPhone } = payload;

				if (newFormattedPhone && newUnformattedPhone) {
					setPhoneNumberLoaded(true);

					if (parentPhoneLoaded !== undefined) { // sf_12700_sfdc_paid_landing_refresh
						setParentPhoneNumber({ number: newFormattedPhone, unformattedNumber: newUnformattedPhone });
						setParentPhoneLoaded(true);
					}
				}

				setFormattedPhoneNumber(newFormattedPhone);
				setUnformattedPhoneNumber(newUnformattedPhone);
			})
			.catch(() => {
				setOutlineLoadingBtn(outline);
				setFetchingPhoneNumber(false);
			});
	};

	const PhoneComponent = showAsLink ? PhoneNumber : PhoneNumberButton;
	if (OMIAppRentalCompleted) {
		return (
			<PhoneComponent
				className={classnames('click-to-show-number-button', className)}
				outline={outline}
				size={size}
				dark={dark}
				color={color}
				phoneNumber={facilityPhone}
				unformattedPhoneNumber={unformattedFacilityPhone}
				hideIcon={hideIcon}
				link={showAsLink}
				clickable
			/>
		);
	}

	const useParentPhoneNumber = parentPhoneLoaded && parentPhoneNumber?.number && parentPhoneNumber?.unformattedNumber; // sf_12700_sfdc_paid_landing_refresh
	return !phoneNumberLoaded ? (
		<Button
			className={classnames('click-to-show-number-button', className)}
			onClick={handleFetchPhoneNumber}
			disabled={fetchingPhoneNumber}
			loading={fetchingPhoneNumber}
			size={size}
			dark={dark}
			outline={outlineLoadingBtn}
			link={showAsLink}
			preContent={!hideIcon &&
			<Phone
				size={size}
			/>
			}
		>
			<div className="phone-number">
				{callToActionText || (
					<Fragment>
						<div className="hide-for-mobile">
							{label || 'Show phone number'}
						</div>
						<div className="hide-for-desktop">
							{ mobileLabel || label || 'Call now'}
						</div>
					</Fragment>
				)}
			</div>
		</Button>
	) : (
		<PhoneComponent
			className={classnames('click-to-show-number-button', className)}
			outline={outline}
			size={size}
			dark={dark}
			color={color}
			phoneNumber={!useParentPhoneNumber ? formattedPhoneNumber : parentPhoneNumber.number}
			unformattedPhoneNumber={!useParentPhoneNumber ? unformattedPhoneNumber : parentPhoneNumber.unformattedNumber}
			hideIcon={hideIcon}
			link={showAsLink}
			clickable
		/>
	);
}

ClickToShowNumberButton.propTypes = {
	callToActionText: PropTypes.string,
	className: PropTypes.string,
	showAsLink: PropTypes.bool,
	hideIcon: PropTypes.bool,
	outline: PropTypes.bool,
	size: PropTypes.oneOf(['tiny', 'small', 'standard', 'medium', 'large', 'extra-large', 'huge', 'giant']),
	color: PropTypes.string,
	dark: PropTypes.bool,
	OMIAppRentalCompleted: PropTypes.bool,
	facilityPhone: PropTypes.string,
	unformattedFacilityPhone: PropTypes.string,
	fetchPhoneNumber: PropTypes.func,
	// sf_12700_sfdc_paid_landing_refresh
	label: PropTypes.string,
	mobileLabel: PropTypes.string,
	parentPhoneNumber: PropTypes.object,
	parentPhoneLoaded: PropTypes.bool,
	setParentPhoneNumber: PropTypes.func,
	setParentPhoneLoaded: PropTypes.func
};

export default ClickToShowNumberButton;
